import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    deleteDoc,
    query,
    where
} from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface SavedContract {
    id: string; // noticeId from SAM.gov
    contractorId: string;
    savedBy: string; // user uid
    savedAt: string;

    // Contract data from SAM.gov
    title: string;
    agency?: string;
    solicitationNumber?: string;
    postedDate?: string;
    responseDeadline?: string;
    type?: string;
    naicsCode?: string;
    uiLink?: string;

    // User annotations
    notes?: string;
    priority?: 'high' | 'medium' | 'low';
    status?: 'new' | 'review' | 'bid' | 'submitted' | 'won' | 'lost';
}

const SAVED_CONTRACTS_COLLECTION = 'savedContracts';

// Get all saved contracts for a contractor
// Note: Using simple query + client-side sort to avoid needing composite index during dev
export async function getSavedContracts(contractorId: string): Promise<SavedContract[]> {
    const q = query(
        collection(db, SAVED_CONTRACTS_COLLECTION),
        where('contractorId', '==', contractorId)
    );

    const snapshot = await getDocs(q);
    const contracts = snapshot.docs.map(doc => doc.data() as SavedContract);

    // Sort by savedAt descending on client side
    return contracts.sort((a, b) =>
        new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime()
    );
}

// Get saved contract IDs for quick lookup (for starring UI)
export async function getSavedContractIds(contractorId: string): Promise<Set<string>> {
    const contracts = await getSavedContracts(contractorId);
    return new Set(contracts.map(c => c.id));
}

// Check if a specific contract is saved
export async function isContractSaved(contractorId: string, noticeId: string): Promise<boolean> {
    const docRef = doc(db, SAVED_CONTRACTS_COLLECTION, `${contractorId}_${noticeId}`);
    const docSnap = await getDoc(docRef);
    return docSnap.exists();
}

// Save (star) a contract - strips undefined values for Firestore
export async function saveContract(contract: SavedContract): Promise<void> {
    const docId = `${contract.contractorId}_${contract.id}`;
    const docRef = doc(db, SAVED_CONTRACTS_COLLECTION, docId);

    // Clean object: remove undefined values (Firestore doesn't allow them)
    const cleanContract: Record<string, any> = {};
    Object.entries(contract).forEach(([key, value]) => {
        if (value !== undefined) {
            cleanContract[key] = value;
        }
    });

    cleanContract.savedAt = new Date().toISOString();

    await setDoc(docRef, cleanContract);
}

// Remove (unstar) a contract
export async function unsaveContract(contractorId: string, noticeId: string): Promise<void> {
    const docId = `${contractorId}_${noticeId}`;
    const docRef = doc(db, SAVED_CONTRACTS_COLLECTION, docId);
    await deleteDoc(docRef);
}

// Toggle star status
export async function toggleSaveContract(
    contract: Omit<SavedContract, 'savedAt'>,
    isSaved: boolean
): Promise<boolean> {
    if (isSaved) {
        await unsaveContract(contract.contractorId, contract.id);
        return false;
    } else {
        await saveContract({
            ...contract,
            savedAt: new Date().toISOString(),
        });
        return true;
    }
}

// Update contract notes or status
export async function updateSavedContract(
    contractorId: string,
    noticeId: string,
    updates: Partial<SavedContract>
): Promise<void> {
    const docId = `${contractorId}_${noticeId}`;
    const docRef = doc(db, SAVED_CONTRACTS_COLLECTION, docId);

    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
        await setDoc(docRef, { ...docSnap.data(), ...updates }, { merge: true });
    }
}

// Get counts by status
export async function getSavedContractStats(contractorId: string): Promise<{
    total: number;
    byStatus: Record<string, number>;
    byPriority: Record<string, number>;
}> {
    const contracts = await getSavedContracts(contractorId);

    const byStatus: Record<string, number> = {};
    const byPriority: Record<string, number> = {};

    contracts.forEach(c => {
        const status = c.status || 'new';
        const priority = c.priority || 'medium';
        byStatus[status] = (byStatus[status] || 0) + 1;
        byPriority[priority] = (byPriority[priority] || 0) + 1;
    });

    return {
        total: contracts.length,
        byStatus,
        byPriority,
    };
}
